/// <amd-module name="TFS/Admin/Views/ProfileSettings/AuthorizationsView/Components/RevokeButtonColumn" />
import * as React from "react";
import * as Resources from "TFS/Admin/Views/ProfileSettings/AuthorizationsView/Resources";
import { Button } from "VSSUI/Button";
import { css, eventTargetContainsNode, KeyCode, preventDefault } from "VSSUI/Util";

import { IAuthorizationViewModel } from "TFS/Admin/Views/ProfileSettings/AuthorizationsView/Contracts/DataModel";
import { ITableColumn, TableColumnLayout } from "VSSUI/Table";

/**
 * The RevokeButtonColumn class is used as a custom column with a revoke button within a Table.
 *
 * It renders a fixed width column with a Revoke button that is visible on focus and
 * hover. The caller should create a new instance of this for each table. The header for this
 * column is empty.
 *
 * When creating the column you must supply delegate that is used to activate onClick when the
 * button is clicked.
 */
export class RevokeButtonColumn<T extends IAuthorizationViewModel> implements ITableColumn<T> {
    private onActivate: (listItem: IAuthorizationViewModel) => void;
    public columnLayout = TableColumnLayout.none;
    public id = "btn-revoke";
    public width = 42;
    public readonly name: string = Resources.Revoke;

    /**
     * Creates an instance of RevokeButtonColumn with the appropriate delegates.
     *
     * @param onActivate method that is called when the Revoke button is clicked
     */
    constructor(onActivate: (listItem: T) => void) {
        this.onActivate = onActivate;
    }

    public renderCell = (rowIndex: number, columnIndex: number, tableColumn: ITableColumn<T>, listItem: T): JSX.Element => {
        return (
            <td
                aria-colindex={columnIndex + 1}
                className={css("bolt-table-cell-side-action bolt-table-cell bolt-list-cell", "col-" + columnIndex)}
                data-column-index={columnIndex}
                key="col-revoke"
                onClick={this.onClick}
                onDoubleClick={preventDefault}
                onKeyDown={this.onKeyDown}
                onMouseDown={preventDefault}
            >
                <div className="bolt-table-cell-content-reveal flex-row justify-center">
                    <Button
                        ariaLabel={Resources.Revoke}
                        excludeTabStop={true}
                        iconProps={{ iconName: "Delete" }}
                        onClick={e => {
                            this.onActivate(listItem);
                        }}
                        subtle={true}
                        tooltipProps={{ text: Resources.Revoke }}
                    />
                </div>
            </td>
        );
    };

    public renderHeaderCell = (columnIndex: number, tableColumn: ITableColumn<T>): JSX.Element => {
        return (
            <th
                aria-colindex={columnIndex + 1}
                className={css(tableColumn.headerClassName, "bolt-table-header-cell bolt-table-header-cell-empty", "col-header-" + columnIndex)}
                data-column-index={columnIndex}
                key="col-revoke"
            />
        );
    };

    private onClick = (event: React.MouseEvent<HTMLElement>) => {
        if (!event.defaultPrevented) {
            if (!eventTargetContainsNode(event, ["A"], document.body)) {
                event.preventDefault();
            }
        }
    };

    private onKeyDown = (event: React.KeyboardEvent<HTMLElement>) => {
        if (!event.defaultPrevented) {
            if (event.which === KeyCode.enter || event.which === KeyCode.space) {
                if (!eventTargetContainsNode(event, ["A"])) {
                    event.preventDefault();
                }
            }
        }
    };
}
